<template>
  <div class="ui-ai-conversation" :class="mainClasses">
    <div class="ui-ai-conversation__header">
      <div class="ui-ai-conversation__caption">
        <span v-if="started">Allposit AI agent</span>
      </div>
      <div class="ui-ai-conversation__actions">
        <template v-if="started">
          <UIButtonCopy
            :disabled="loading"
            variant="gray"
            fill="light"
            size="xs"
            :text="copyMessages"
          />
        </template>
        <slot name="actions" :started :disabled="loading" />
      </div>
    </div>
    <div class="ui-ai-conversation__area">
      <div v-if="started" ref="contentRef" class="ui-ai-conversation__content">
        <AIConversationMessage
          v-for="(message, index) in messages"
          :key="index"
          :message
        >
          <template v-if="$slots.message" #message>
            <slot
              name="message"
              :message
              :is-last="index === messages.length - 1"
              :handle-click-example
            />
          </template>
        </AIConversationMessage>
        <div v-if="loading" class="ui-ai-conversation__loading" />
        <slot name="messages" />
      </div>
      <div class="ui-ai-conversation__form">
        <div v-if="!started" class="ui-ai-conversation__caption">
          I'm the Allposit AI agent. What can I do for you?
        </div>
        <AIConversationForm
          ref="formRef"
          :loading
          :started
          @submit="handleSubmit"
        />
        <AIConversationExamples
          v-if="!started && examples"
          :examples
          :disabled="loading"
          @click:example="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watch, nextTick } from 'vue'

import type { AIMessage } from '@types'

import AIConversationMessage from './AIConversationMessage.vue'
import AIConversationForm from './AIConversationForm.vue'
import AIConversationExamples from './AIConversationExamples.vue'

import { UIButtonCopy } from '@ui/buttons'

type Props = {
  loading?: boolean
  messages: AIMessage[]
  examples?: string[]
}

type Emits = {
  submit: [message: string, callback?: () => void]
  reset: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  reset() {
    formRef.value?.reset()
  },
})

const contentRef = useTemplateRef('contentRef')
const formRef = useTemplateRef('formRef')


const copyMessages = computed(() =>
  JSON.stringify(
    props.messages.map(item => {
      let message = item.content
      try {
        message = JSON.parse(message)
      } catch {}
      return {
        role: item.role,
        message,
      }
    }),
    null,
    2,
  ),
)

const started = computed(() => props.messages?.length > 0)

const mainClasses = computed(() => {
  return {
    'ui-ai-conversation--started': started.value,
  }
})

const handleSubmit = async (data: string, callback?: () => void) => {
  emit('submit', data, callback)
}

const handleClickExample = (message: string) => {
  formRef.value?.appendMessage(message)
}

watch(
  () => props.loading,
  () => {
    nextTick(() => {
      contentRef.value?.scrollTo({
        top: contentRef.value?.scrollHeight,
      })
    })
  },
)
</script>

<style>
.ui-ai-conversation {

  &__main {
    @apply w-full;
    @apply flex flex-col flex-auto;
    @apply px-7 pb-7;
  }

  &__area {
    @apply w-full sm:max-w-xl lg:max-w-3xl xl:max-w-4xl;
    @apply flex flex-col flex-auto;
    @apply gap-4 mx-auto;
  }

  &__header {
    @apply flex items-center justify-between;
    @apply py-2.5;

    .ui-button {
      @apply rounded-full;
    }
  }

  &__caption {
    @apply font-medium;
    @apply text-base;
  }

  &__actions {
    @apply flex items-center gap-2;
  }

  &__content {
    @apply flex-auto;
    @apply flex flex-col items-start;
    @apply px-2;
    @apply overflow-y-auto;
  }

  &__loading {
    @apply size-3;
    @apply mb-4;
    @apply shrink-0;
    @apply bg-primary dark:bg-primary-selected;
    @apply rounded-full;
    @apply animate-ping;
  }

  &__form {
    @apply flex flex-col gap-2;
    @apply pt-4 px-2;
  }

  &__form &__caption {
    @apply text-center;
  }
}
</style>
